import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { ODDS_FORMATS } from "../lib/format";
import { isOddsFormat, setOddsFormat, useOddsFormat } from "../lib/odds-format";
import { signOut } from "../lib/omauth";
import { setPriceMode, usePriceMode } from "../lib/price-mode";
import { ConnectButton } from "./ConnectButton";
import { Logo } from "./Logo";

export interface LeagueTab {
  id: string;
  name: string;
  count: number;
}

/**
 * Logo, search + league tabs, taker-fee switch, odds format, sign out. On a phone the search and
 * tabs drop to their own scrolling row under the logo.
 */
export function TopBar({
  tabs,
  active,
  onTab,
  query,
  onQuery,
  signedIn,
}: {
  tabs: LeagueTab[];
  /** The active tab id; empty while searching. */
  active: string;
  onTab: (id: string) => void;
  query: string;
  onQuery: (q: string) => void;
  /** Signed out, the account button is the sign-in CTA. */
  signedIn: boolean;
}) {
  const format = useOddsFormat();
  const mode = usePriceMode();
  const taker = mode === "taker";

  return (
    <header
      className="z-20 shrink-0 border-b border-line bg-bg/70 backdrop-blur-xl"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:flex-nowrap sm:gap-4 sm:px-4">
        <Logo />
        <nav
          aria-label="Leagues"
          className="no-scrollbar order-last -mx-3 flex w-[calc(100%+1.5rem)] items-center gap-1 overflow-x-auto px-3 sm:order-none sm:mx-0 sm:w-auto sm:min-w-0 sm:flex-1 sm:px-0"
        >
          <SearchBox value={query} onChange={onQuery} />
          <Tab active={active === "hot"} onClick={() => onTab("hot")}>
            <Flame />
            Hot
          </Tab>
          {tabs.map((t) => (
            <Tab key={t.id} active={active === t.id} onClick={() => onTab(t.id)}>
              {t.name}
              <span className={cn("text-[11px] font-semibold", active === t.id ? "opacity-70" : "text-faint")}>
                {t.count}
              </span>
            </Tab>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPriceMode(taker ? "listed" : "taker")}
            aria-pressed={taker}
            title="Show every price after the venue's taker fee — what it actually costs to take it right now"
            className={cn(
              "flex items-center gap-2 rounded-full border py-1.5 pl-2 pr-3 text-[12px] font-semibold transition-colors",
              taker
                ? "brand-gradient border-transparent text-accent-ink"
                : "border-line text-dim hover:border-cell-hi hover:text-ink",
            )}
          >
            <span
              aria-hidden
              className={cn(
                "relative h-4 w-7 rounded-full transition-colors",
                taker ? "bg-accent-ink/30" : "bg-cell-hi",
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 h-3 w-3 rounded-full transition-all",
                  taker ? "left-3.5 bg-accent-ink" : "left-0.5 bg-dim",
                )}
              />
            </span>
            <span className="hidden sm:inline">Taker fees</span>
            <span className="sm:hidden">Fees</span>
          </button>
          <label htmlFor="odds-format" className="sr-only">
            Odds format
          </label>
          <select
            id="odds-format"
            value={format}
            onChange={(e) => {
              if (isOddsFormat(e.target.value)) setOddsFormat(e.target.value);
            }}
            className="rounded-full border border-line bg-panel px-3 py-1.5 text-[12px] font-semibold text-ink outline-none transition-colors hover:border-cell-hi focus-visible:border-accent"
          >
            {ODDS_FORMATS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {!signedIn ? (
            <ConnectButton label="Sign in" />
          ) : (
          <button
            type="button"
            onClick={signOut}
            title="Sign out"
            className="flex items-center gap-1.5 rounded-full border border-line px-2.5 py-1.5 text-[12px] font-semibold text-dim transition-colors hover:border-cell-hi hover:text-ink"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path
                d="M6 3H4.5A1.5 1.5 0 003 4.5v7A1.5 1.5 0 004.5 13H6M10 11l3-3-3-3M13 8H6.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="hidden md:inline">Sign out</span>
          </button>
          )}
        </div>
      </div>
    </header>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="relative mr-1 flex shrink-0 items-center">
      <span className="sr-only">Search games</span>
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden
        className="pointer-events-none absolute left-3 text-faint"
      >
        <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search games"
        className={cn(
          "w-36 rounded-full border bg-panel py-1.5 pl-8 pr-3 text-[13px] text-ink outline-none transition-all placeholder:text-faint sm:w-40 sm:focus:w-56",
          value ? "border-accent sm:w-56" : "border-line focus:border-accent",
        )}
      />
    </label>
  );
}

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-all",
        active
          ? "brand-gradient text-accent-ink shadow-[0_4px_18px_-6px_var(--color-accent)]"
          : "text-dim hover:bg-cell hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

function Flame() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M8.6 1.2c.3 2.2-.7 3.4-1.7 4.5C5.8 6.9 4.6 8.1 4.6 10.2A3.9 3.9 0 008.5 14a3.9 3.9 0 003.9-3.9c0-1.5-.6-2.6-1.3-3.4-.1 1-.6 1.8-1.4 2.2.3-2.9-.5-5.6-1.1-7.7z" />
    </svg>
  );
}
